

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {IErrors} from "@/model/IErrors";
import {ILocker} from "@/model/Locker/lockerInterfaces";
import LockerChip from "@/components/shared/chips/LockerChip.vue";
import {IOnOffHandlers} from "bonomo/dist";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import {IUserImported} from "@/store/modules/userImportModule";

const lockerModule = namespace("lockerModule");
const userImportModule = namespace("userImportModule");
const authModule = namespace("authModule");

@Component({
  components: {
    LockerChip
  },
})

export default class UserImport extends Vue {

  @userImportModule.Action("exportUsers")
  private exportUsers!: (lockerId: number|null) => void;
  @userImportModule.Getter('importLockers')
  private lockers!: ILocker[];
  @userImportModule.Getter('importResults')
  private importResults!: IUserImported[];
  @userImportModule.Getter('errors')
  private errors!: IErrors;

  private readonly OKstatuses: number[] = [0, 101, 102, 103];


  protected get exampleLink(): string {
    return '/modulo/assets/resources/importExample-' + this.$i18n.locale + '.csv'
  }
  protected get hasImportResults(): boolean {
    return this.importResults && !!this.importResults.length;
  }
  protected get hasErrors(): boolean {
    return this.errors && !!Object.keys(this.errors).length;
  }
  protected get showResults(): boolean {
    return this.hasImportResults || this.hasErrors;
  }
  protected get resultsImportedCnt(): number {
    return this.hasImportResults
      ? this.importResults.filter(each => each.code_import == 101).length
      : 0;
  }
  protected get resultsUpdatedCnt(): number {
    return this.hasImportResults
      ? this.importResults.filter(each => each.code_import == 102).length
      : 0;
  }
  protected get resultsNotModifiedCnt(): number {
    return this.hasImportResults
      ? this.importResults.filter(each => each.code_import == 103).length
      : 0;
  }
  protected get resultsErrorsCnt(): number {
    return this.hasImportResults
      ? this.importResults.filter(each => this.isErrorResult(each)).length
      : 0;
  }
  protected get importResultsOrdered(): IUserImported[] {
    return this.importResults && this.importResults.length
      ? this.importResults.filter(each => this.isErrorResult(each))
          .concat(
              this.importResults.filter(each => !this.isErrorResult(each))
          )
      : [];
  }
  private isErrorResult(result: IUserImported): boolean {
    return !this.OKstatuses.includes(result.code_import);
  }

  protected get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('users.userImport.line_number'), align: 'start', sortable: false, value: 'line' },
      { text: this.$t('users.userList.id'), align: 'start', sortable: false, value: 'id' },
      { text: this.$t('users.userList.email'), align: 'start', sortable: false, value: 'email' },
      { text: this.$t('users.userList.firstName'), align: 'start', sortable: false, value: 'first_name' },
      { text: this.$t('users.userList.lastName'), align: 'start', sortable: false, value: 'last_name' },
      { text: this.$t('users.userList.phone'), align: 'start', sortable: false, value: 'phone_number' },
      { text: this.$t('lockers.locker.title'), align: 'start', sortable: false, value: 'locker_id' },
      { text: this.$t('users.userImport.delivery_token'), align: 'start', sortable: false, value: 'deliveryToken' },
      { text: this.$t('users.userImport.resultImported'), align: 'start', sortable: false, value: 'code_import' },
    ];
  }
  protected rules: any[] = [];
  protected file: any = null;
  protected lockerSelected: number = 0;
  protected loading: boolean = true;
  protected submitting: boolean = false;

  private subscribes: IOnOffHandlers = {};

  private errorFieldMap: any = {
    10: 'code_import', // unknown
    13: 'email', // header
    100: 'phone_number', // invalid line
    101: 'code_import', // OK,
    102: 'code_import', // updated
    103: 'code_import', // exists
    105: 'phone_number',
    130: 'code_import', // imported with error
    201: 'email',
    202: 'first_name',
    203: 'last_name',
    204: 'phone_number',
    254: 'phone_number',
  };

  protected fieldClass(item: IUserImported, currentField: string = ''): string[] {

    const result = item.code_import;

    const ret: string[] = ['user-import--result-cell pl-3 pr-3'];

    // consider success both cases
    if ((item.code_import === 101) || (item.code_import === 130) || (item.code_import === 0)) {
      ret.push('green');
    }
    // not updated
    else if (item.code_import === 102) {
      ret.push('blue');
    }
    else if (item.code_import === 103) {
      ret.push('teal');
    }
    // not processed
    else if (item.code_import === 199) {
      ret.push('yellow');
    }
    else {
      ret.push('red');
    }

    const isErrorTargetField = this.errorFieldMap[result] === currentField;

    // keep the field that caused the error bright, lighten the rest of the line
    ret.push(
        isErrorTargetField ||
        (result === 100 && ((item as any)[currentField]) === '')
          ? 'white--text'
          : 'lighten-4'
    );

    // extra padding if not locker field
    if (currentField !== 'locker_id') {
      ret.push('pt-1 pb-1');
    }

    return ret;

  }

  // created / beforeDestroy are always the first
  created() {

    // one reason we don't really use vue's validation are the lame rules.
    this.rules.push(
        (v: any) => !!v || this.$t('errors.general.required'),
    );

    this.subscribes = this.$ons({
      'login.OK': this.loadData,
      'backend.call.OK.importLockersLoaded': this.lockersLoaded,
      'backend.call.OK.usersImported': this.imported,
      'backend.call.KO.usersImported': this.imported,
      'backend.call.OK.exportUsersLoaded': this.exported,
    });

    this.loadData();

  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  // always needs abstraction as it must be called in created() and on 'login.OK' event
  private loadData() {
    this.loading = true;
    this.$store.dispatch('userImportModule/fetchImportLockers');
  }

  private lockersLoaded() {
    this.loading = false;
    if (this.lockers && (this.lockers.length === 1)) {
      this.lockerSelected = this.lockers[0].id;
    }
  }

  // have to enable button, and clean previous file
  private imported() {
    this.loading = false;
    this.submitting = false;
    // clean file, and reset validation so it won't turn red by being null
    this.file = null;
    (this.$refs.form as any).resetValidation();
  }

  protected onSubmit(){

    // @ts-ignore (another reason why we don't like v-form :) )
    if (this.$refs.form.validate()) {
      this.loading = true;
      this.submitting = true;
      this.$store.dispatch('userImportModule/importUsers', {
        file: this.file,
        lockerId: this.lockerSelected
      });
    }

  }

  protected addFile(file: any){
    this.file = file;
  }

  // moved here
  protected findLocker(lockerId: number): ILocker {
    const locker = this.lockers.find((each: ILocker) => each.id == lockerId);
    return locker ? locker : {} as ILocker;
  }

  protected exportUsersClicked() {
    this.submitting = true;
    this.exportUsers(this.lockerSelected || null);
  }

  private exported() {
    this.submitting = false;
  }
}

